import { calculateHeaderGroupHeight } from "@theme/critical";
import { Component } from "@theme/component";
import { onDocumentLoaded, changeMetaThemeColor } from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerDrawerContainer", "headerMenu", "headerRowTop"];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Minimum scroll distance required before toggling sticky state, to avoid flicker near boundaries
   * @type {number}
   */
  #scrollDeltaTolerance = 50;

  /**
   * Scroll distance from the bottom of the page where the header should remain hidden to avoid flicker
   * @type {number}
   */
  #bottomBuffer = 50;

  /**
   * Whether we are within the bottom buffer zone; used to avoid flicker from small upward scrolls
   * @type {boolean}
   */
  #withinBottomBuffer = false;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty("--header-height", `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? "inactive" : "active";
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        // Header can intersect the viewport while still idle (hidden). Without
        // resetting, #offscreen becomes false and scroll handling stops, leaving
        // the menu invisible until the user scrolls down and back up again.
        if (isIntersecting && this.dataset.stickyState === "idle") {
          this.dataset.stickyState = "inactive";
          this.dataset.scrollDirection = "none";
          this.removeAttribute("data-animating");
        }

        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === "active";
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove("desktop:hidden");
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add("hidden");
    } else {
      this.refs.headerDrawerContainer.classList.add("desktop:hidden");
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove("hidden");
    }
  }

  #handleWindowScroll = () => {
    this.#updatePageTopState();

    const stickyMode = this.getAttribute("sticky");
    if (!this.#offscreen && stickyMode !== "always") {
      if (this.dataset.stickyState === "idle") {
        this.dataset.stickyState = "inactive";
        this.dataset.scrollDirection = "none";
        this.removeAttribute("data-animating");
      }
      return;
    }

    const scrollingElement =
      document.scrollingElement ?? document.documentElement;

    const scrollTop = scrollingElement?.scrollTop ?? 0;
    const clientHeight = scrollingElement?.clientHeight ?? 0;
    const scrollHeight = scrollingElement?.scrollHeight ?? 0;
    const isNearBottom =
      scrollTop + clientHeight >= scrollHeight - this.#bottomBuffer;
    const scrollDelta = scrollTop - this.#lastScrollTop;
    const isScrollingUp = scrollDelta < 0;
    const scrollTolerance =
      isScrollingUp && this.dataset.stickyState === "idle"
        ? 5
        : this.#scrollDeltaTolerance;
    const hasSignificantDelta = Math.abs(scrollDelta) > scrollTolerance;

    if (stickyMode !== "always") {
      if (isNearBottom) {
        this.#withinBottomBuffer = true;
      } else if (this.#withinBottomBuffer) {
        const nearBottomBufferLimit =
          scrollHeight - this.#bottomBuffer - this.#scrollDeltaTolerance;
        if (scrollTop + clientHeight >= nearBottomBufferLimit) {
          this.#lastScrollTop = scrollTop;
          return;
        }

        this.#withinBottomBuffer = false;
      }
    }

    if (this.#withinBottomBuffer && stickyMode !== "always") {
      this.dataset.scrollDirection = "down";
      this.dataset.stickyState = "idle";
      this.removeAttribute("data-animating");
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!hasSignificantDelta && stickyMode !== "always") {
      return;
    }
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === "always") {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = "none";
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = "up";
      } else {
        this.dataset.scrollDirection = "down";
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute("data-animating");

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = "inactive";
        this.dataset.scrollDirection = "none";
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "up";
      }
    } else if (this.dataset.stickyState === "active") {
      // immediately hide the sticky header when scrolling down
      this.dataset.scrollDirection = "down";
      this.dataset.stickyState = "idle";
      this.removeAttribute("data-animating");
    } else {
      this.dataset.scrollDirection = "none";
      this.dataset.stickyState = "idle";
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);
    this.#updatePageTopState();
    document.addEventListener("scroll", this.#updatePageTopState);

    const stickyMode = this.getAttribute("sticky");
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === "always");

      if (stickyMode === "scroll-up" || stickyMode === "always") {
        document.addEventListener("scroll", this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);
    document.removeEventListener("scroll", this.#updatePageTopState);
    document.removeEventListener("scroll", this.#handleWindowScroll);
    document.body.style.setProperty("--header-height", "0px");
  }

  #updatePageTopState = () => {
    const scrollingElement =
      document.scrollingElement ?? document.documentElement;
    const scrollTop = scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    document.documentElement.toggleAttribute("data-page-at-top", scrollTop <= 0);
  };
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector("#header-component");
  const headerGroup = document.querySelector("#header-group");

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() =>
      calculateHeaderGroupHeight(header, headerGroup)
    );

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement))
              continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }

  // Click-toggle header selector dropdowns; close on outside click or scroll while open.
  /** @type {Map<HTMLElement, (isOpen: boolean) => void>} */
  const selectorDropdowns = new Map();

  document.querySelectorAll(".language-selector__custom").forEach((dropdown) => {
    if (!(dropdown instanceof HTMLElement)) return;

    const toggle = dropdown.querySelector("[aria-expanded]");
    if (!(toggle instanceof HTMLElement)) return;

    /** @type {AbortController | null} */
    let openAbort = null;

    const setOpen = (isOpen) => {
      toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
      openAbort?.abort();
      openAbort = null;

      if (!isOpen) return;

      // Close every other header selector dropdown.
      for (const [otherDropdown, otherSetOpen] of selectorDropdowns) {
        if (otherDropdown !== dropdown) otherSetOpen(false);
      }

      openAbort = new AbortController();
      const { signal } = openAbort;

      document.addEventListener(
        "scroll",
        () => setOpen(false),
        { passive: true, capture: true, signal }
      );

      // Defer so the opening click does not immediately count as an outside click.
      requestAnimationFrame(() => {
        document.addEventListener(
          "click",
          (event) => {
            if (event.target instanceof Node && !dropdown.contains(event.target)) {
              setOpen(false);
            }
          },
          { signal }
        );
      });
    };

    selectorDropdowns.set(dropdown, setOpen);

    toggle.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      setOpen(toggle.getAttribute("aria-expanded") !== "true");
    });
  });
});
